import React from "react";
import { Link } from "react-router-dom";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";

/**
 * Directory card grid for organizations / creators on Community pages.
 */
function CommunityDirectoryGrid({
  items = null,
  loading = false,
  error = false,
  emptyTitle = "Nothing to show yet",
  emptyBody = "Profiles will appear here as communities publish.",
  onRetry = null,
  kindLabel = "Profile",
}) {
  const list = Array.isArray(items) ? items : [];

  if (loading) {
    return (
      <div
        className="community-directory"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="community-directory__grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={`dir-skel-${i}`}
              className="community-directory__card community-directory__card--skeleton"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="community-directory community-directory--error" role="alert">
        <h3 className="community-directory__empty-title">
          Couldn’t load the directory
        </h3>
        <p className="community-directory__empty-body">
          Please try again in a moment.
        </p>
        {typeof onRetry === "function" ? (
          <button
            type="button"
            className="btn btn-danger"
            onClick={onRetry}
          >
            Retry
          </button>
        ) : (
          <Link to="/home" className="btn btn-outline-secondary">
            Back to Home
          </Link>
        )}
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="community-directory community-directory--empty">
        <h3 className="community-directory__empty-title">{emptyTitle}</h3>
        <p className="community-directory__empty-body">{emptyBody}</p>
        <div className="community-directory__actions">
          <Link to="/home" className="btn btn-danger">
            Explore Home
          </Link>
          <Link to="/platforms" className="btn btn-outline-secondary">
            Platform Availability
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="community-directory">
      <ul className="community-directory__grid">
        {list.map((item, index) => {
          const path =
            item.path ||
            item.public_path ||
            item.href ||
            "#";
          const name = item.name || item.display_name || item.title || kindLabel;
          const image =
            item.logo_path || item.avatar || item.cover_image_path || item.banner;
          const meta =
            item.organization_type_label ||
            item.category ||
            (item.kind === "creator" ? "Creator" : null) ||
            (item.kind === "organization" ? "Organization" : null) ||
            (item.kind === "project" ? "Project" : null);
          const badges = [];
          if (item.is_featured) {
            badges.push("Featured");
          }
          if (item.is_verified) {
            badges.push("Verified");
          }

          return (
            <li
              key={item.id || item.slug || `dir-${index}`}
              className="community-directory__card-wrap"
            >
              <Link
                to={path}
                className="community-directory__card"
                aria-label={`View ${name}`}
              >
                <div className="community-directory__media" aria-hidden={!image}>
                  {image ? (
                    <img
                      src={resolveMediaUrl(image)}
                      alt=""
                      onError={handleMediaImgError}
                    />
                  ) : (
                    <span className="community-directory__media-fallback">
                      {(name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="community-directory__body">
                  {meta ? (
                    <p className="community-directory__meta">{meta}</p>
                  ) : null}
                  <h3 className="community-directory__name">{name}</h3>
                  {item.summary || item.description ? (
                    <p className="community-directory__summary">
                      {item.summary || item.description}
                    </p>
                  ) : null}
                  {badges.length > 0 ? (
                    <p className="community-directory__badges">
                      {badges.join(" · ")}
                    </p>
                  ) : null}
                  <span className="community-directory__cta">View profile</span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CommunityDirectoryGrid;
